import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Formik } from "formik";
import * as Yup from "yup";
import {createHomeOverview, getHomeOverview, updateHomeOverview} from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";

// Validation Schema
const validationSchema = Yup.object({
  icon1: Yup.mixed()
    .test("required", "Icon 1 is required", function (value) {
      const { old_icon1 } = this.parent;
      return !!(value || old_icon1);
    }),
  icon2: Yup.mixed()
    .test("required", "Icon 2 is required", function (value) {
      const { old_icon2 } = this.parent;
      return !!(value || old_icon2);
    }),
  icon3: Yup.mixed()
    .test("required", "Icon 3 is required", function (value) {
      const { old_icon3 } = this.parent;
      return !!(value || old_icon3);
    })
    .test("fileType", "Only image files are allowed", (value) => {
      if (!value || typeof value === 'string') return true;
      return ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'].includes(value.type);
    }),
  icon4: Yup.mixed()
    .test("required", "Icon 4 is required", function (value) {
      const { old_icon4 } = this.parent;
      return !!(value || old_icon4);
    }),
  iconTitle1: Yup.string()
    .required("Title 1 is required"),
  iconTitle2: Yup.string()
    .required("Title 2 is required"),
  iconTitle3: Yup.string()
    .required("Title 3 is required"),
  iconTitle4: Yup.string()
    .required("Title 4 is required"),
});

export default function OverviewEdit() {
  const { id, partnerId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [overview, setOverview] = useState({
    icon1: "",
    iconTitle1: "",
    icon2: "",
    iconTitle2: "",
    icon3: "",
    iconTitle3: "",
    icon4: "",
    iconTitle4: "",
    index: null,
  });
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  // Initial form values
  const getInitialValues = () => ({
    icon1: overview.icon1 || "",
    icon2: overview.icon2 || "",
    icon3: overview.icon3 || "",
    icon4: overview.icon4 || "",
    iconTitle1: overview.name1 || "",
    iconTitle2: overview.name2 || "",
    iconTitle3: overview.name3 || "",
    iconTitle4: overview.name4 || "",
    old_icon1: overview.icon1 || "",
    old_icon2: overview.icon2 || "",
    old_icon3: overview.icon3 || "",
    old_icon4: overview.icon4 || "",
  });

  const fetchOverviewData = async () => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await getHomeOverview(id);
      const overviewData = response.data.data[0];
      setOverview({
        ...overviewData,
        index: response.data.data.indexOf(overviewData),
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: error.response.data.message || 'Failed to fetch overview data. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOverviewData();
  }, [id]);

  const handleSubmit = async (values, { setFieldValue }) => {
    setSaving(true);

    var formData = new FormData();
    formData.append("name1", values.iconTitle1);
    formData.append("name2", values.iconTitle2);
    formData.append("name3", values.iconTitle3);
    formData.append("name4", values.iconTitle4);

    if (values.icon1 instanceof File) {
      formData.append("icon1", values.icon1);
    }

    if (values.icon2 instanceof File) {
      formData.append("icon2", values.icon2);
    }

    if (values.icon3 instanceof File) {
      formData.append("icon3", values.icon3);
    }

    if (values.icon4 instanceof File) {
      formData.append("icon4", values.icon4);
    }

    if (!Number(id)) {
      try {
        formData.append("partner_id", partnerId);
        const res = await createHomeOverview(formData);
        console.log(res);
        
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || 'Service has been created successfully!',
          });
          const overviewData = res?.data?.data;
          setOverview(overviewData);
          if (overviewData && overviewData.id) {
            navigate(`/${partnerId}/home/overview/edit/${overviewData.id}`);
          }
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || 'Failed to create service overview section. Please try again.',
          });
        }
      } catch (err) {
        console.log(err);
        showToast({
          type: 'error',
          message: err?.response?.data?.message || 'Failed to create service overview section. Please try again.',
        });
      }
    } else {
      try {
        const res = await updateHomeOverview(id, formData);
        console.log(res);
        
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || 'Service has been updated successfully!',
          });
          
          // Update the form fields with the returned icon URLs if available
          const updatedOverviewData = res?.data?.data;
          if (updatedOverviewData) {
            if (updatedOverviewData.icon1) {
              setFieldValue("icon1", updatedOverviewData.icon1);
              setFieldValue("old_icon1", updatedOverviewData.icon1);
            }
            if (updatedOverviewData.icon2) {
              setFieldValue("icon2", updatedOverviewData.icon2);
              setFieldValue("old_icon2", updatedOverviewData.icon2);
            }
            if (updatedOverviewData.icon3) {
              setFieldValue("icon3", updatedOverviewData.icon3);
              setFieldValue("old_icon3", updatedOverviewData.icon3);
            }
            if (updatedOverviewData.icon4) {
              setFieldValue("icon4", updatedOverviewData.icon4);
              setFieldValue("old_icon4", updatedOverviewData.icon4);
            }
            setOverview(prev => ({
              ...prev,
              ...updatedOverviewData,
            }));
          }
          
          // Clear the file inputs
          inputRefs.forEach(ref => {
            if (ref.current) ref.current.value = '';
          });
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || 'Failed to update service overview section. Please try again.',
          });
        }
      } catch (err) {
        console.log(err);
        showToast({
          type: 'error',
          message: err?.response?.data?.message || 'Failed to update service overview section. Please try again.',
        });
      }
    }
    
    setSaving(false);
  };

  return (
   <>
    {isLoading ? <CmsCircularProgress/> : 
    <FormWrapper>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          getFieldProps,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Title>{Number(id) ? 'Edit' : 'Add' } Service Overview section</Title>
            <SubTitle>Icon 1</SubTitle>
            <FormGroup
              label="Select an icon 1"
              requiredField
              error={touched.icon1 && errors.icon1 ? errors.icon1 : ''}
            >
              <Form.Control
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setFieldValue("icon1", file);
                }}
                onBlur={getFieldProps('icon1').onBlur}
                style={{ display: "none" }}
                ref={inputRefs[0]}
                name="icon1"
                isInvalid={touched.icon1 && !!errors.icon1}
              />
              {values.icon1 ? (
                <ImageWrapper>
                  <UploadImage>
                    <BootstrapImage
                      src={values.icon1 instanceof File ? URL.createObjectURL(values.icon1) : values.icon1}
                      alt="icon1"
                      onClick={() => {
                        inputRefs[0].current.click();
                      }}
                    />
                  </UploadImage>
                  {(values.icon1 instanceof File || (values.icon1 && values.icon1 !== values.old_icon1)) && (
                    <RemoveButtonWrapper>
                      <BootstrapButton
                        type="button"
                        onClick={() => {
                          setFieldValue("icon1", values.old_icon1 || null);
                          if (inputRefs[0].current) {
                            inputRefs[0].current.value = '';
                          }
                        }}
                      >
                        <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                      </BootstrapButton>
                    </RemoveButtonWrapper>
                  )}
                </ImageWrapper>
              ) : (Number(id) && (values.icon1 || values.old_icon1)) ? (
                <UploadImage>
                  <BootstrapImage
                    src={values.icon1 || values.old_icon1}
                    alt="icon1"
                    onClick={() => {
                      inputRefs[0].current.click();
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = 0.8;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = 1;
                    }}
                  />
                </UploadImage>
              ) : (
                <div>
                  <Button
                    className="w-auto mb-0"
                    onClick={() => {
                      inputRefs[0].current.click();
                    }}
                  >
                    Upload the image
                  </Button>
                </div>
              )}
            </FormGroup>
       
            <FormGroup 
              label='Enter a title 1'
              requiredField
              error={touched.iconTitle1 && errors.iconTitle1 ? errors.iconTitle1 : ''}
            >
              <Form.Control
                as="textarea"
                rows={2}
                {...getFieldProps('iconTitle1')}
                isInvalid={touched.iconTitle1 && !!errors.iconTitle1}
              />
            </FormGroup>

            <SubTitle className="mt-2">Icon 2</SubTitle>
            <FormGroup
              label="Select an icon 2"
              requiredField
              error={touched.icon2 && errors.icon2 ? errors.icon2 : ''}
            >
              <Form.Control
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setFieldValue("icon2", file);
                }}
                onBlur={getFieldProps('icon2').onBlur}
                style={{ display: "none" }}
                ref={inputRefs[1]}
                name="icon2"
                isInvalid={touched.icon2 && !!errors.icon2}
              />
              {values.icon2 ? (
                <ImageWrapper>
                  <UploadImage>
                    <BootstrapImage
                      src={values.icon2 instanceof File ? URL.createObjectURL(values.icon2) : values.icon2}
                      alt="icon2"
                      onClick={() => {
                        inputRefs[1].current.click();
                      }}
                    />
                  </UploadImage>
                  {(values.icon2 instanceof File || (values.icon2 && values.icon2 !== values.old_icon2)) && (
                    <RemoveButtonWrapper>
                      <BootstrapButton
                        type="button"
                        onClick={() => {
                          setFieldValue("icon2", values.old_icon2 || null);
                          if (inputRefs[1].current) {
                            inputRefs[1].current.value = '';
                          }
                        }}
                      >
                        <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                      </BootstrapButton>
                    </RemoveButtonWrapper>
                  )}
                </ImageWrapper>
              ) : (Number(id) && (values.icon2 || values.old_icon2)) ? (
                <UploadImage>
                  <BootstrapImage
                    src={values.icon2 || values.old_icon2}
                    alt="icon2"
                    onClick={() => {
                      inputRefs[1].current.click();
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = 0.8;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = 1;
                    }}
                  />
                </UploadImage>
              ) : (
                <div>
                  <Button
                    className="w-auto mb-0"
                    onClick={() => {
                      inputRefs[1].current.click();
                    }}
                  >
                    Upload the image
                  </Button>
                </div>
              )}
            </FormGroup>

            <FormGroup 
              className="mb-3" 
              label='Enter a title 2'
              requiredField
              error={touched.iconTitle2 && errors.iconTitle2 ? errors.iconTitle2 : ''}
            >
              <Form.Control
                as="textarea"
                rows={2}
                {...getFieldProps('iconTitle2')}
                isInvalid={touched.iconTitle2 && !!errors.iconTitle2}
              />
            </FormGroup>
     
            <SubTitle className="mt-3">Icon 3</SubTitle>
            <FormGroup
              label="Select an icon 3"
              requiredField
              error={touched.icon3 && errors.icon3 ? errors.icon3 : ''}
            >
              <Form.Control
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setFieldValue("icon3", file);
                }}
                onBlur={getFieldProps('icon3').onBlur}
                style={{ display: "none" }}
                ref={inputRefs[2]}
                name="icon3"
                isInvalid={touched.icon3 && !!errors.icon3}
              />
              {values.icon3 ? (
                <ImageWrapper>
                  <UploadImage>
                    <BootstrapImage
                      src={values.icon3 instanceof File ? URL.createObjectURL(values.icon3) : values.icon3}
                      alt="icon3"
                      onClick={() => {
                        inputRefs[2].current.click();
                      }}
                    />
                  </UploadImage>
                  {(values.icon3 instanceof File || (values.icon3 && values.icon3 !== values.old_icon3)) && (
                    <RemoveButtonWrapper>
                      <BootstrapButton
                        type="button"
                        onClick={() => {
                          setFieldValue("icon3", values.old_icon3 || null);
                          if (inputRefs[2].current) {
                            inputRefs[2].current.value = '';
                          }
                        }}
                      >
                        <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                      </BootstrapButton>
                    </RemoveButtonWrapper>
                  )}
                </ImageWrapper>
              ) : (Number(id) && (values.icon3 || values.old_icon3)) ? (
                <UploadImage>
                  <BootstrapImage
                    src={values.icon3 || values.old_icon3}
                    alt="icon3"
                    onClick={() => {
                      inputRefs[2].current.click();
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = 0.8;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = 1;
                    }}
                  />
                </UploadImage>
              ) : (
                <div>
                  <Button
                    className="w-auto mb-0"
                    onClick={() => {
                      inputRefs[2].current.click();
                    }}
                  >
                    Upload the image
                  </Button>
                </div>
              )}
            </FormGroup>

            <FormGroup 
              label={'Enter a title 3'}
              requiredField
              error={touched.iconTitle3 && errors.iconTitle3 ? errors.iconTitle3 : ''}
            >
              <Form.Control
                as="textarea"
                rows={2}
                {...getFieldProps('iconTitle3')}
                isInvalid={touched.iconTitle3 && !!errors.iconTitle3}
              />
            </FormGroup>

            <SubTitle className="mt-3">Icon 4</SubTitle>
            <FormGroup
              label='Select an icon 4'
              requiredField
              error={touched.icon4 && errors.icon4 ? errors.icon4 : ''}
            >
              <Form.Control
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setFieldValue("icon4", file);
                }}
                onBlur={getFieldProps('icon4').onBlur}
                style={{ display: "none" }}
                ref={inputRefs[3]}
                name="icon4"
                isInvalid={touched.icon4 && !!errors.icon4}
              />
              {values.icon4 ? (
                <ImageWrapper>
                  <UploadImage>
                    <BootstrapImage
                      src={values.icon4 instanceof File ? URL.createObjectURL(values.icon4) : values.icon4}
                      alt="icon4"
                      onClick={() => {
                        inputRefs[3].current.click();
                      }}
                    />
                  </UploadImage>
                  {(values.icon4 instanceof File || (values.icon4 && values.icon4 !== values.old_icon4)) && (
                    <RemoveButtonWrapper>
                      <BootstrapButton
                        type="button"
                        onClick={() => {
                          setFieldValue("icon4", values.old_icon4 || null);
                          if (inputRefs[3].current) {
                            inputRefs[3].current.value = '';
                          }
                        }}
                      >
                        <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                      </BootstrapButton>
                    </RemoveButtonWrapper>
                  )}
                </ImageWrapper>
              ) : (Number(id) && (values.icon4 || values.old_icon4)) ? (
                <UploadImage>
                  <BootstrapImage
                    src={values.icon4 || values.old_icon4}
                    alt="icon4"
                    onClick={() => {
                      inputRefs[3].current.click();
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = 0.8;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = 1;
                    }}
                  />
                </UploadImage>
              ) : (
                <div>
                  <Button
                    className="w-auto mb-0"
                    onClick={() => {
                      inputRefs[3].current.click();
                    }}
                  >
                    Upload the image
                  </Button>
                </div>
              )}
            </FormGroup>

            <FormGroup 
              label='Enter a title 4'
              requiredField
              error={touched.iconTitle4 && errors.iconTitle4 ? errors.iconTitle4 : ''}
            >
              <Form.Control
                as="textarea"
                rows={2}
                {...getFieldProps('iconTitle4')}
                isInvalid={touched.iconTitle4 && !!errors.iconTitle4}
              />
            </FormGroup>
            <br />
            <ButtonWrapper>
              <Link to={`/${partnerId}/home/overview/view/${Number(id) ? id : "create"}`}>
                <Button variant="secondary" className="w-100">Go Back</Button>
              </Link>
              <Button
                variant="primary"
                type="submit"
                disabled={saving || isSubmitting}
                className="w-100"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </ButtonWrapper>
          </Form>
        )}
      </Formik>
    </FormWrapper> }
   </>
  );
}

const FormWrapper = styled.div`
max-width: 865px;
margin:auto;
padding:10px;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const SubTitle = styled.h5``;

const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
 position: absolute;
    top: -17px;
    right: -14px;
       @media (max-width:576px) {
          top: -9px;
        right: -17px;
  }
`;
const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "10vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "40vw",
  },
}));
const  BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  "&:hover": {
    background: '#fffeff',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  @media (max-width: 890px) {
  gap: 2rem;
  }
 @media (max-width: 425px) {
  gap: 1rem;
}
`;
const UploadImage = styled.div`
  max-width: 10vw;
   overflow: hidden;
    border-radius: 6px;
    @media (max-width:576px) {
   max-width: 40vw;
  }
`;
const ImageWrapper = styled.div`
    position:relative;
    max-width: 10vw;
   @media (max-width:576px) {
   max-width: 40vw;
  }
`;
